import { createReducer, on } from '@ngrx/store';
import { AllocationGroup, InventoryAllocation } from '../../interfaces/allocation-group';

import {
  fetchUserAllocationGroups,
  fetchUserAllocationGroupsSuccess,
  fetchUserAllocationGroupsFailure,
  fetchProductAllocations,
  fetchProductAllocationsSuccess,
  fetchProductAllocationsFailure,
  fetchProductsAllocations,
  fetchProductsAllocationsSuccess,
  fetchProductsAllocationsFailure,
  resetProductAllocations,
} from '../actions/inventory-allocations.actions';

export interface IProductAllocationEntry {
  allocations: InventoryAllocation[];
  fetching: boolean;
  loaded: boolean;
  // The request failed, as opposed to succeeding with nothing to offer. Without this the two are
  // the same state, and a backend outage reads to the user as "you have no allocation groups".
  error: boolean;
}

export interface IInventoryAllocationsState {
  userAllocationGroups: AllocationGroup[];
  fetchingUserAllocationGroups: boolean;
  userAllocationGroupsLoaded: boolean;
  byProductId: { [productId: number]: IProductAllocationEntry };
}

export const initialState: IInventoryAllocationsState = {
  userAllocationGroups: [],
  fetchingUserAllocationGroups: false,
  userAllocationGroupsLoaded: false,
  byProductId: {},
};

const emptyEntry: IProductAllocationEntry = {
  allocations: [],
  fetching: false,
  loaded: false,
  error: false,
};

export const inventoryAllocationsReducer = createReducer(
  initialState,
  on(fetchUserAllocationGroups, (state) => ({
    ...state,
    fetchingUserAllocationGroups: true,
  })),
  on(fetchUserAllocationGroupsSuccess, (state, { groups }) => ({
    ...state,
    fetchingUserAllocationGroups: false,
    userAllocationGroupsLoaded: true,
    userAllocationGroups: groups,
  })),
  on(fetchUserAllocationGroupsFailure, (state) => ({
    ...state,
    fetchingUserAllocationGroups: false,
  })),
  on(fetchProductAllocations, (state, { productId }) => {
    const existing = state.byProductId[productId] ?? emptyEntry;
    return {
      ...state,
      byProductId: {
        ...state.byProductId,
        [productId]: {
          allocations: existing.allocations,
          loaded: existing.loaded,
          fetching: true,
          error: false,
        },
      },
    };
  }),
  on(fetchProductAllocationsSuccess, (state, { productId, allocations }) => ({
    ...state,
    byProductId: {
      ...state.byProductId,
      [productId]: { allocations, fetching: false, loaded: true, error: false },
    },
  })),
  on(fetchProductAllocationsFailure, (state, { productId }) => ({
    ...state,
    byProductId: {
      ...state.byProductId,
      [productId]: { ...(state.byProductId[productId] ?? emptyEntry), fetching: false, error: true },
    },
  })),
  on(fetchProductsAllocations, (state, { productIds }) => ({
    ...state,
    byProductId: productIds.reduce((byProductId, productId) => {
      const existing = state.byProductId[productId] ?? emptyEntry;
      byProductId[productId] = {
        allocations: existing.allocations,
        loaded: existing.loaded,
        fetching: true,
        // a retry clears the previous failure, so a recovered backend un-sticks the dropdowns
        error: false,
      };
      return byProductId;
    }, { ...state.byProductId }),
  })),
  // Every key of the map is written, empty ones included. A product the response left out has no
  // allocation groups to offer, and it has to land as loaded so its dropdown stops loading and
  // says "No items found" instead.
  on(fetchProductsAllocationsSuccess, (state, { allocationsByProductId }) => ({
    ...state,
    byProductId: Object.keys(allocationsByProductId).reduce((byProductId, productId) => {
      byProductId[productId] = {
        allocations: allocationsByProductId[productId] ?? [],
        fetching: false,
        loaded: true,
        error: false,
      };
      return byProductId;
    }, { ...state.byProductId }),
  })),
  on(fetchProductsAllocationsFailure, (state, { productIds }) => ({
    ...state,
    byProductId: productIds.reduce((byProductId, productId) => {
      byProductId[productId] = { ...(state.byProductId[productId] ?? emptyEntry), fetching: false, error: true };
      return byProductId;
    }, { ...state.byProductId }),
  })),
  on(resetProductAllocations, (state, { productId }) => ({
    ...state,
    byProductId: {
      ...state.byProductId,
      [productId]: { allocations: [], fetching: false, loaded: false, error: false },
    },
  })),
);
